import React from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { Link } from "react-router-dom";

export default function SingleVerticalMaritime() {
  return (
    <div className="single-vertical-parent">
      <div className="vertical-title">
        <div className="text-900 font-bold text-7xl mb-4 text-center">
          FlexNet Maritime: Enabling Smart Connectivity at Sea
        </div>
        <div className="text-700 text-xl mb-6 text-center line-height-3">
          Optimize vessel operations, crew safety, and offshore communications
          with FlexNet’s private 4G/5G maritime solutions.
        </div>
      </div>
      {/* Hero Section */}
      <div className="grid grid-nogutter surface-section text-800 ">
        <div className="col-12 md:col-6 p-6 text-center md:text-left flex align-items-center">
          <section>
            <span className="block text-6xl font-bold mb-3">
              FlexNet Maritime
            </span>
            <div className="text-6xl text-primary font-bold mb-3">
              Revolutionizing Operations with 5G Connectivity
            </div>
            <p className="mt-0 mb-4 text-700 line-height-3">
              Ensure seamless maritime connectivity with FlexNet's private
              mobile networks. Enhance ship operations, real-time monitoring,
              and crew communication even beyond coastal limits.
            </p>
            <button className="flex underline align-items-center mt-4">
              <Link to="/connectivity">Request Demo</Link>
              <FaExternalLinkAlt className="ml-2" />
            </button>
          </section>
        </div>
        <div className="col-12 md:col-6 overflow-hidden">
          <img
            src="https://www.shutterstock.com/shutterstock/videos/3651750827/thumb/1.jpg?ip=x480"
            alt="hero-1"
            className="md:ml-auto block md:h-full"
            style={{ clipPath: "polygon(8% 0, 100% 0%, 100% 100%, 0 100%)" }}
          />
        </div>
      </div>

      {/* Key Benefits Section */}
      <div className="surface-section px-4 py-8 md:px-6 lg:px-8 text-center">
        <div className="mb-3 font-bold text-3xl">
          <span className="text-900">Why </span>
          <span className="text-blue-600">FlexNet for Maritime?</span>
        </div>
        <div className="grid justify-center">
          {keyBenefits.map((benefit, index) => (
            <div key={index} className="col-12 md:col-4 mb-4 px-5">
              <span
                className="p-3 shadow-2 mb-3 inline-block surface-card"
                style={{ borderRadius: "10px" }}
              >
                <i className={`${benefit.icon} text-4xl text-blue-500`}></i>
              </span>
              <div className="text-900 text-xl mb-3 font-medium">
                {benefit.title}
              </div>
              <span className="text-700 line-height-3">{benefit.detail}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Use Cases Section */}

      <div className="mb-3 font-bold text-3xl">
        <span className="text-900">FlexNet Maritime </span>
        <span className="text-blue-600">Use Cases</span>
      </div>
      {useCases.map((useCase, index) => (
        <div
          key={index}
          className="surface-section px-4 py-8 md:px-6 lg:px-8 text-center"
        >
          <div className="b-3 font-bold text-2xl mb-3">
            <span className="text-blue-600">Use Case</span>
            {useCase.title}
          </div>
          <div className="text-700 mb-6">{useCase.description}</div>
          <div className="grid">
            <div className="col-12 md:col-4 mb-4 px-5">
              <div className="text-900 text-xl mb-3 font-medium">Benefits</div>
              {useCase.benefits.map((item, idx) => (
                <div key={idx} className="mb-2">
                  <i className={`${item.icon} text-blue-500 mr-2`}></i>
                  {item.title}
                </div>
              ))}
            </div>
            <div className="col-12 md:col-4 mb-4 px-5">
              <div className="text-900 text-xl mb-3 font-medium">
                Devices/Technologies
              </div>
              {useCase.devices.map((item, idx) => (
                <div key={idx} className="mb-2">
                  <i className={`${item.icon} text-blue-500 mr-2`}></i>
                  {item.title}
                </div>
              ))}
            </div>
            <div className="col-12 md:col-4 mb-4 px-5">
              <div className="text-900 text-xl mb-3 font-medium">
                Applications
              </div>
              {useCase.applications.map((item, idx) => (
                <div key={idx} className="mb-2">
                  <i className={`${item.icon} text-blue-500 mr-2`}></i>
                  {item.title}
                </div>
              ))}
            </div>
            <button className="flex underline align-items-center mt-4 vertical-market-link">
              <Link to="/marketplace">Request Access to Marketplace</Link>
              <FaExternalLinkAlt className="ml-2" />
            </button>
          </div>
        </div>
      ))}

      {/* Call-to-Action Section */}
      <div className="surface-section text-center py-8">
        <div className="text-900 text-3xl font-bold mb-4">
          Ready to Modernize Your Maritime Operations?
        </div>

        <button className="p-button p-button-raised connectivity-request-demo-button p-3">
          <Link to="contact-page">Get in Touch for a Personalized Demo</Link>
        </button>
      </div>
    </div>
  );
}

const keyBenefits = [
  {
    title: "Reliable Offshore Connectivity Beyond 5 KM from the Coast",
    detail: "Ensure continuous network access even in deep-sea environments.",
    icon: "pi pi-globe",
  },
  {
    title: "Enhanced Crew Communication with Push-to-Talk (PTT) Solutions",
    detail: "Enable seamless voice communication across the vessel.",
    icon: "pi pi-comments",
  },
  {
    title: "Real-Time Video Surveillance and Monitoring",
    detail: "Improve security and operational oversight with live streaming.",
    icon: "pi pi-video",
  },
  {
    title: "IoT Integration for Ship Systems and Safety Automation",
    detail: "Leverage real-time monitoring and predictive maintenance.",
    icon: "pi pi-cog",
  },
  {
    title:
      "Secure, Low-Latency Data Transmission with Reliable Uplink Performance",
    detail: "Ensure stable data flow for mission-critical maritime operations.",
    icon: "pi pi-lock",
  },
];

const useCases = [
  {
    title: " 1: Maritime Private Network for Vessel Operations",
    description:
      "Deploy private 4G/5G networks onboard to ensure continuous connectivity for operational systems, even in international waters.",
    benefits: [
      {
        title: "Reliable communication beyond 10 KM from the ship",
        icon: "pi pi-globe",
      },
      { title: "Enhanced data throughput", icon: "pi pi-chart-line" },
      {
        title: "Secure connectivity without dependency on external operators",
        icon: "pi pi-lock",
      },
    ],
    devices: [
      { title: "5G Core", icon: "pi pi-server" },
      { title: "Small Cells", icon: "pi pi-wifi" },
      { title: "Omnidirectional Antennas", icon: "pi pi-signal" },
      { title: "LEO Satellite", icon: "pi pi-globe" },
    ],
    applications: [
      { title: "Vessel Communication Platforms", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 2: Crew Communication with Push-to-Talk (PTT)",
    description:
      "Enable efficient crew coordination with 5G-enabled Push-to-Talk (PTT) systems, ensuring seamless voice communication across the ship.",
    benefits: [
      {
        title: "Instant communication for safety-critical operations",
        icon: "pi pi-phone",
      },
      {
        title: "Reliable PTT functionality in all ship areas",
        icon: "pi pi-comments",
      },
      {
        title: "Improved crew collaboration during emergencies",
        icon: "pi pi-exclamation-triangle",
      },
    ],
    devices: [
      { title: "Rugged PTT Devices", icon: "pi pi-mobile" },
      { title: "Network Gateways", icon: "pi pi-cloud" },
    ],
    applications: [
      { title: "Crew Safety and Communication Systems", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 3: Video Surveillance and Remote Monitoring",
    description:
      "Implement high-definition video surveillance with real-time streaming over private networks to enhance security and operational oversight.",
    benefits: [
      {
        title: "Real-time monitoring of critical ship areas",
        icon: "pi pi-video",
      },
      {
        title: "Enhanced safety with proactive incident detection",
        icon: "pi pi-shield",
      },
      {
        title: "Secure data transmission to onshore control centers",
        icon: "pi pi-lock",
      },
    ],
    devices: [
      { title: "5G-enabled HD Cameras", icon: "pi pi-camera" },
      { title: "Edge Servers", icon: "pi pi-server" },
    ],
    applications: [
      { title: "Maritime Surveillance Systems", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 4: IoT-Based Ship Automation",
    description:
      "Leverage IoT sensors for real-time monitoring of ship systems, including engine performance, fuel levels, and environmental conditions.",
    benefits: [
      {
        title: "Predictive maintenance to reduce downtime",
        icon: "pi pi-chart-line",
      },
      { title: "Efficient energy management", icon: "pi pi-bolt" },
      {
        title: "Enhanced automation for critical ship functions",
        icon: "pi pi-cog",
      },
    ],
    devices: [
      { title: "IoT Sensors", icon: "pi pi-cloud" },
      { title: "Actuators", icon: "pi pi-cog" },
      { title: "Control Units", icon: "pi pi-server" },
    ],
    applications: [
      { title: "Ship Management Platforms", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 5: Satellite-Integrated Connectivity",
    description:
      "Integrate private networks with satellite communication systems to maintain global connectivity, especially in deep-sea environments.",
    benefits: [
      {
        title: "Uninterrupted data access regardless of location",
        icon: "pi pi-globe",
      },
      {
        title: "Reliable failover options for critical communication",
        icon: "pi pi-shield",
      },
      {
        title: "Seamless data transmission between ship and shore",
        icon: "pi pi-wifi ",
      },
    ],
    devices: [
      { title: "Satellite Antennas", icon: "pi pi-globe " },
      { title: "Network Gateways", icon: "pi pi-cloud" },
      { title: "Fiber Links", icon: "pi pi-wifi" },
    ],
    applications: [
      { title: "Maritime Data Communication Systems", icon: "pi pi-desktop" },
    ],
  },
];
